<script setup>
import { computed, onMounted } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store.js';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import Checkbox from 'next/checkbox/Checkbox.vue';
import Spinner from 'next/spinner/Spinner.vue';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const uiFlags = getters['wgptIntegrations/getUIFlags'];
const zidWebhooks = getters['wgptIntegrations/getZidWebhooks'];

const isLoading = computed(() => {
  if (zidWebhooks.value.length) return false;
  return uiFlags.isFetchingZidWebhooks;
});

const webhooks = computed(() => {
  return zidWebhooks.value.map(webhook => {
    const eventKey = webhook.event_name.replace(/\./g, '_').toUpperCase();
    return {
      ...webhook,
      // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
      label: t(
        `WGPT_INTEGRATIONS.ZID.EDITOR.SETTINGS.WEBHOOKS.EVENTS.${eventKey}`
      ),
    };
  });
});

const fetchWebhooks = () => {
  store.dispatch('wgptIntegrations/getZidWebhooks');
};

const handleCheckboxChange = async (subscribed, eventName) => {
  try {
    const data = {
      eventName,
      subscribed,
    };
    await store.dispatch('wgptIntegrations/updateZidWebhookSubscription', data);

    const successMessage = t(
      'WGPT_INTEGRATIONS.ZID.EDITOR.API.SUCCESS_MESSAGE'
    );
    useAlert(successMessage);
  } catch (error) {
    const errorMessage =
      error?.message || t('WGPT_INTEGRATIONS.ZID.EDITOR.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

onMounted(() => {
  fetchWebhooks();
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-col gap-2">
      <h3 class="text-base font-medium leading-6 text-n-slate-12">
        <span class="truncate">{{
          t('WGPT_INTEGRATIONS.ZID.EDITOR.SETTINGS.WEBHOOKS.TITLE')
        }}</span>
      </h3>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('WGPT_INTEGRATIONS.ZID.EDITOR.SETTINGS.WEBHOOKS.DESCRIPTION') }}
      </p>
    </div>

    <div v-if="isLoading" class="flex justify-center my-4">
      <Spinner class="text-n-brand" />
    </div>

    <div v-else class="grid gap-3 mt-2">
      <div
        v-for="webhook in webhooks"
        :key="webhook.event_name"
        class="flex items-center gap-2"
      >
        <Checkbox
          v-model="webhook.subscribed"
          :disabled="uiFlags.isUpdatingZid"
          @change="
            event =>
              handleCheckboxChange(event.target.checked, webhook.event_name)
          "
        />
        <label
          :for="`checkbox-${webhook.event_name}`"
          class="text-sm text-n-slate-12 font-normal"
        >
          {{ webhook.label }}
        </label>
      </div>
    </div>
  </div>
</template>
